/*
OpenAI Ads measurement pixel.

Set up from OpenAI's own instructions (31 Aug 2026). Two halves exist in their
docs and only the browser one lives here:

  - THIS FILE: the `oaiq` pixel, which reports a conversion from the visitor's
    browser. The pixel id is a public identifier, like a GA4 measurement id, so
    it ships in the bundle.
  - NOT HERE: their Conversions API, which posts the same events server-side and
    is what makes attribution survive ad blockers. It authenticates with a
    Bearer key, and every REACT_APP_* var is compiled into the JavaScript we
    serve — so putting that key anywhere in this repo would publish it. It
    belongs in the backend, keyed off the same event id.

The pixel is inert until REACT_APP_OPENAI_PIXEL_ID is set, exactly like GA4:
nothing loads, nothing is sent, and no third-party script reaches a visitor who
arrives before we are ready to advertise.
*/

const SDK_SRC = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

/** The configured pixel id, or "" when advertising on this channel is off. */
export function openAiPixelId(env = process.env) {
  const id = env.REACT_APP_OPENAI_PIXEL_ID;
  return typeof id === "string" && id.trim() ? id.trim() : "";
}

/**
 * Install the queue stub and load the SDK. Safe to call more than once.
 *
 * The stub is theirs verbatim: calls made before the script arrives are queued
 * on `oaiq.q` and replayed by the SDK. That ordering is the point — a sign-up
 * that happens during the redirect back from Clerk must not be lost because a
 * CDN was slow.
 */
export function initOpenAiPixel(win = typeof window !== "undefined" ? window : null, doc = typeof document !== "undefined" ? document : null, env = process.env) {
  const pixelId = openAiPixelId(env);
  if (!pixelId || !win || !doc) return false;
  if (win.__oaiqInstalled) return true;

  try {
    win.oaiq = win.oaiq || function oaiq(...args) {
      (win.oaiq.q = win.oaiq.q || []).push(args);
    };
    win.oaiq("init", { pixelId });

    const script = doc.createElement("script");
    script.async = true;
    script.src = SDK_SRC;
    doc.head.appendChild(script);
    win.__oaiqInstalled = true;
    return true;
  } catch (e) {
    return false; // analytics must never break the page
  }
}

/**
 * Report a conversion.
 *
 * `eventId` is the deduplication key. It matters because the same conversion is
 * meant to be sent twice — once from here and once from the server — and OpenAI
 * collapses them by this id. It also makes a retry safe: our sign-up path can
 * re-send after a failed delivery without counting the person twice.
 *
 * @param {string} eventName - must match the conversion event created in Ads Manager.
 * @param {object} [data] - event payload, e.g. { type: "contents" }.
 * @param {string} [eventId] - stable id for deduplication.
 * @returns {boolean} whether the call was handed to the pixel.
 */
export function measureOpenAiConversion(eventName, data = {}, eventId, win = typeof window !== "undefined" ? window : null) {
  if (!eventName || !win || typeof win.oaiq !== "function") return false;
  try {
    win.oaiq("measure", eventName, data, eventId ? { event_id: eventId } : {});
    return true;
  } catch (e) {
    return false;
  }
}
